/**
 * Pronto · Sopralluogo Live + Annuncio vocale (Studio mode).
 *
 * Il file principale è un sottile orchestratore che gestisce lo stato
 * di fase (setup → recording → review) e delega la UI ai 3 sotto-componenti
 * estratti in `./sopralluogo/`:
 *   - SetupPhase           — input opzionali città/indirizzo/tipologia
 *   - RecordingPhase       — registratore audio + checklist + foto
 *   - ReviewPhase          — estratto + form pre-conversione + queue kit
 *   - CheckListPromemoria  — bullet "Cosa dire" (usato da RecordingPhase)
 *   - shared.js            — costanti + helpers
 *
 * iter v28 r298 — Refactor: file ridotto da 1356 → ~80 righe.
 * iter v32 r49g — User M1454: «se mi salta il popup crediti e esco, perdo
 * tutto». Aggiunto auto-resume: al mount controlliamo GET /sopralluogo/active
 * e se c'è una sessione aperta entro 24h offriamo un banner «Riprendi il
 * sopralluogo iniziato N min fa». Le foto e le trascrizioni erano già su
 * DB; mancava solo il modo di ritrovarle dopo aver chiuso la tab.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// eslint-disable-next-line no-unused-vars
import { toast } from "sonner";
import { ArrowClockwise, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import ReopenGuidesBtn from "@/components/ReopenGuidesBtn";
import SetupPhase from "./sopralluogo/SetupPhase";
import RecordingPhase from "./sopralluogo/RecordingPhase";
import ReviewPhase from "./sopralluogo/ReviewPhase";

function _formatAgo(isoStr) {
  if (!isoStr) return "poco fa";
  try {
    const then = new Date(isoStr).getTime();
    const mins = Math.max(1, Math.round((Date.now() - then) / 60000));
    if (mins < 60) return `${mins} min fa`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} or${hrs === 1 ? "a" : "e"} fa`;
    return "oltre 24h fa";
  } catch {
    return "poco fa";
  }
}

export default function Sopralluogo() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("setup"); // setup | recording | review
  const [session, setSession] = useState(null);
  const [finalResult, setFinalResult] = useState(null);
  // iter v32 r49g — Banner "Riprendi sopralluogo" se ne trovo uno aperto.
  const [activeResume, setActiveResume] = useState(null); // {active, word_count, photo_count, created_at}
  const [resumeDismissed, setResumeDismissed] = useState(false);

  // iter 242: modalità "studio" via query param ?mode=studio → l'agente non è
  // sul posto, sta dettando l'annuncio da remoto (in studio, in macchina, dal
  // cliente). Cambia solo il framing (eyebrow, titolo, sottotitolo): il flow
  // sotto (recording + review) è identico.
  const isStudio = new URLSearchParams(window.location.search).get("mode") === "studio";
  // iter 297: se la pagina è aperta da DimmiTab > Voce dentro un annuncio
  // esistente, leggiamo ?property_id=… e lo passiamo come target_property_id
  // alla conversione finale: il backend fa merge invece di creare un nuovo
  // immobile, evitando duplicati.
  const targetPropertyId = new URLSearchParams(window.location.search).get("property_id") || null;

  // iter v32 r49g — Al mount: cerca sopralluoghi aperti entro 24h.
  // Skip se stiamo arrivando con ?property_id (Voice from DimmiTab,
  // contesto separato) o se siamo già in recording/review (improbabile).
  useEffect(() => {
    if (targetPropertyId || phase !== "setup") return;
    let cancelled = false;
    api.get("/sopralluogo/active")
      .then((r) => {
        if (cancelled) return;
        // iter v33 r56 · Il backend ora espone `converting_at` a livello
        // top del payload per distinguere "sessione in registrazione" da
        // "generazione annuncio interrotta". Lo salviamo nell'activeResume
        // cosi' isInterruptedConvert() puo' calcolarlo senza fetch extra.
        if (r.data?.active) setActiveResume(r.data);
      })
      .catch(() => { /* silent; recovery è best-effort */ });
    return () => { cancelled = true; };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const resumeActive = () => {
    if (!activeResume?.active) return;
    setSession({ ...activeResume.active, id: activeResume.active.id });
    setPhase("recording");
    setActiveResume(null);
  };

  // v34 · 29 ago 2026 · User: «se ne creo uno nuovo cancella quello lì
  // che rimane in memoria». Quando l'utente sceglie "Inizia uno nuovo"
  // (o "Chiudi banner"), oltre a dismissare l'UI eliminiamo lato server
  // la sessione orfana così NON ritorna al prossimo mount. Best-effort:
  // se la delete fallisce, proseguiamo comunque (backend `/sopralluogo/start`
  // fa comunque cleanup delle orfane al prossimo start).
  const dismissAndCleanup = async () => {
    const sid = activeResume?.active?.id;
    setResumeDismissed(true);
    setActiveResume(null);
    if (sid) {
      try {
        await api.delete(`/sopralluogo/${sid}`);
      } catch { /* silent, backend fara' cleanup al prossimo start */ }
    }
  };

  // iter v33 r56 · User: «ripristino sessione». Se la sessione ha
  // `converting_at` recente MA nessun `property_id`, l'agente ha premuto
  // "Genera annuncio" ma qualcosa e' andato storto (iOS background, app
  // chiusa a meta'). Bottone dedicato: verifichiamo lato backend se il
  // property_id e' arrivato nel frattempo (LLM potrebbe aver finito) →
  // navighiamo. Altrimenti riprendiamo dalla fase review offrendo un
  // nuovo tentativo di generazione (il lock atomico backend impedisce
  // duplicati).
  const [recoveringConvert, setRecoveringConvert] = useState(false);
  const resumeFromConverting = async () => {
    if (!activeResume?.active?.id) return;
    setRecoveringConvert(true);
    const sid = activeResume.active.id;
    try {
      // Polling breve backend (30s max): se il property_id e' stato creato
      // dopo l'interruzione, lo troviamo qui e navighiamo dentro.
      for (let i = 0; i < 10; i++) {
        try {
          const { data } = await api.get(`/sopralluogo/${sid}`, { timeout: 15000 });
          if (data?.property_id) {
            navigate(`/dashboard/property/${data.property_id}?from=taccuino`);
            return;
          }
        } catch { /* riprovo al prossimo giro */ }
        if (i === 0) toast.info("Verifico se l'annuncio è già pronto…", { duration: 3000 });
        await new Promise((r) => setTimeout(r, 3000));
      }
      // Il backend non ha creato niente nei 30s → apri la review, l'utente
      // ripreme Genera (il lock si sara' liberato → nuovo tentativo pulito).
      setSession({ ...activeResume.active, id: sid });
      setFinalResult(activeResume.active.extracted || { extracted: activeResume.active.extracted });
      setPhase("review");
      setActiveResume(null);
      toast.info("Sessione recuperata · premi «Genera annuncio» per riprovare", { duration: 6000 });
    } finally {
      setRecoveringConvert(false);
    }
  };

  // Helper: distingue una sessione "interrotta a meta' della generazione"
  // (converting_at recente, entro 30 min) da una in "recording" normale.
  const isInterruptedConvert = () => {
    const ct = activeResume?.converting_at;
    if (!ct) return false;
    const ageSec = Math.max(0, Date.now() / 1000 - ct);
    return ageSec > 20 && ageSec < 1800;  // 20s..30min
  };

  const refresh = async () => {
    if (!session) return;
    try {
      const { data } = await api.get(`/sopralluogo/${session.id}`);
      setSession(data);
    } catch {
      // silent; may happen between finalize and navigate
    }
  };

  return (
    <AppShell
      title={isStudio ? "Annuncio vocale" : "Sopralluogo Live"}
      breadcrumb={isStudio ? "Annuncio vocale" : "Sopralluogo · al volo"}
      action={<ReopenGuidesBtn keys={["sopralluogo:studio-intro", "sopralluogo:live-intro"]} testId="sopralluogo-reopen-guides" />}
    >
      {/* iter 303: tasto indietro chiaro quando si è entrati da un annuncio
          esistente (DimmiTab > Voce). Senza questo, l'utente era "bloccato"
          dentro il sopralluogo e doveva usare il pulsante Indietro del
          browser per tornare all'annuncio. */}
      {targetPropertyId && (
        <button
          onClick={() => navigate(`/dashboard/property/${targetPropertyId}`)}
          className="flex items-center gap-1.5 text-[13px] text-[#7C8B6E] hover:text-[#1C1C1A] font-medium mb-4"
          data-testid="sopralluogo-back-to-property"
        >
          ← Torna all&apos;annuncio
        </button>
      )}

      {/* iter v33 r56 · Banner "Generazione interrotta" (arancione):
          appare quando la sessione ha converting_at recente ma nessun
          property_id (l'utente ha premuto Genera ma iOS ha ammazzato la
          request in background). Ha priorita' sopra il banner verde
          standard. */}
      {phase === "setup" && activeResume && !resumeDismissed && isInterruptedConvert() && (
        <div
          className="mb-5 rounded-xl border-2 border-[#C9B27A] bg-[#FBF3E0] p-4 sm:p-5 flex items-start gap-3 shadow-sm"
          data-testid="sopralluogo-resume-converting-banner"
        >
          <ArrowClockwise size={22} weight="bold" className="text-[#8A7745] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-[#1C1C1A]">
              Generazione annuncio interrotta
            </div>
            <div className="text-[12.5px] text-[#5A524A] mt-0.5">
              Hai premuto «Genera» ma qualcosa si è interrotto. Verifico se il tuo annuncio è pronto lo stesso — se manca, riprendiamo da dove eri.
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={resumeFromConverting}
                disabled={recoveringConvert}
                className="px-3.5 py-1.5 bg-[#8A7745] hover:bg-[#75633A] text-white text-[12.5px] font-semibold rounded-md disabled:opacity-70 inline-flex items-center gap-1.5"
                data-testid="sopralluogo-resume-converting-btn"
              >
                {recoveringConvert
                  ? <><ArrowClockwise size={12} weight="bold" className="animate-spin" /> Verifico…</>
                  : <>Verifica adesso</>}
              </button>
              <button
                onClick={dismissAndCleanup}
                className="px-3.5 py-1.5 bg-white border border-[#E8E5DE] hover:border-[#8A7745] text-[#5A524A] text-[12.5px] font-medium rounded-md"
                data-testid="sopralluogo-resume-converting-dismiss"
              >
                Inizia uno nuovo
              </button>
            </div>
          </div>
          <button
            onClick={dismissAndCleanup}
            className="text-[#9A968B] hover:text-[#5A524A] -mr-1 -mt-1"
            aria-label="Chiudi"
            data-testid="sopralluogo-resume-converting-close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
      )}

      {/* iter v32 r49g — Banner "Riprendi sopralluogo aperto" (verde std) */}
      {phase === "setup" && activeResume && !resumeDismissed && !isInterruptedConvert() && (
        <div
          className="mb-5 rounded-xl border-2 border-[#7C8B6E] bg-[#F1F4ED] p-4 sm:p-5 flex items-start gap-3 shadow-sm"
          data-testid="sopralluogo-resume-banner"
        >
          <ArrowClockwise size={22} weight="bold" className="text-[#5C6B4E] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-[#1C1C1A]">
              Hai un sopralluogo aperto · {_formatAgo(activeResume.created_at)}
            </div>
            <div className="text-[12.5px] text-[#5C6B4E] mt-0.5">
              {activeResume.photo_count} foto · {activeResume.word_count} parole già salvate. Niente è andato perso.
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={resumeActive}
                className="px-3.5 py-1.5 bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white text-[12.5px] font-semibold rounded-md"
                data-testid="sopralluogo-resume-btn"
              >
                Riprendi
              </button>
              <button
                onClick={dismissAndCleanup}
                className="px-3.5 py-1.5 bg-white border border-[#E8E5DE] hover:border-[#7C8B6E] text-[#5A524A] text-[12.5px] font-medium rounded-md"
                data-testid="sopralluogo-resume-dismiss-btn"
              >
                Inizia uno nuovo
              </button>
            </div>
          </div>
          <button
            onClick={dismissAndCleanup}
            className="text-[#9A968B] hover:text-[#5A524A] -mr-1 -mt-1"
            aria-label="Chiudi"
            data-testid="sopralluogo-resume-close-btn"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
      )}

      {phase === "setup" && (
        <SetupPhase
          isStudio={isStudio}
          targetPropertyId={targetPropertyId}
          onStart={(data) => {
            setSession({ ...data.session, id: data.session_id, photos: [] });
            setPhase("recording");
          }}
        />
      )}
      {phase === "recording" && session && (
        <RecordingPhase
          session={session}
          onRefresh={refresh}
          onFinalize={(result) => { setFinalResult(result); setPhase("review"); }}
          onDiscard={() => { setSession(null); setFinalResult(null); setPhase("setup"); }}
        />
      )}
      {phase === "review" && session && finalResult && (
        <ReviewPhase
          session={session}
          finalResult={finalResult}
          targetPropertyId={targetPropertyId}
          onBack={() => setPhase("recording")}
          onConvert={(pid, merged, _opts = {}) => {
            // iter v33 r58 · Nuovo opt `goHome: true` per la safety net:
            // quando la creazione annuncio non riesce e nessuna delle
            // strategie di verifica trova un match, invece di lasciare
            // l'agente bloccato sul recap lo porto in Home dove puo'
            // controllare direttamente la lista annunci.
            if (_opts?.goHome || !pid) {
              navigate("/dashboard");
              return;
            }
            // iter v33 r26 · Zero automatismo: il sopralluogo crea l'annuncio
            // e porta subito l'agente dentro. Zero attesa, zero flag, zero
            // banner. Se l'agente vuole migliorare le foto lo fa da qui.
            navigate(`/dashboard/property/${pid}?from=taccuino${merged ? "&merged=1" : ""}`);
          }}
        />
      )}
    </AppShell>
  );
}
